// MCMBigCard — the standard tappable card row used across the MCM
// surfaces (CreateMenu's chooser, Home's Focus Card, future Plan
// surfaces). Extracted from CreateMenu so callers don't have to
// duplicate the visual.
//
// Visual contract:
//   - Theme-aware glass surface (theme.color.glassFillLite + bright
//     glassBorder + theme.shadow.glass). Reads live from the active
//     time-of-day theme, same as the kitchen TileGrid cards, so the
//     surface tracks day → dusk → night without a static cream stuck
//     in the wrong tone (the white-tile-on-night-bg bug).
//   - Forma uppercase kicker pill at the top, accent-tinted.
//   - Title in New Kansas italic 500 (item-title face).
//   - Blurb in Forma muted, line-height 1.5.
//   - Accent arrow (→) on the right edge. Accent color does NOT live
//     on the card border per the no-side-stripe rule in CLAUDE.md.
//
// Optional `rightSlot` lets a caller swap the arrow for a custom
// node (e.g. a live timer countdown on Home's Focus Card).

import { FONT, COLOR } from "../lib/tokens";
import { useTheme as useMCMTheme } from "../lib/theme";

export default function MCMBigCard({
  accent,
  kicker,
  title,
  blurb,
  onClick,
  rightSlot = null,
  // When true, drop the glass dropshadow so the card sits flat on
  // its surrounding background. Used by Home's Focus carousel — the
  // shadow read as overstated against the parchment scroll.
  flat = false,
}) {
  const { theme } = useMCMTheme();
  const accentBg = accent + "1a";       // ~10% alpha tint for kicker pill bg
  const accentBorder = accent + "44";   // ~27% alpha for kicker pill border
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "16px 16px 14px",
        textAlign: "left",
        background: theme.color.glassFillLite,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        border: `1px solid ${theme.color.glassBorder}`,
        borderRadius: 24,
        cursor: "pointer",
        display: "flex",
        alignItems: "stretch",
        gap: 14,
        boxShadow: flat ? "none" : theme.shadow?.glass,
        transition: "border-color 0.18s ease, box-shadow 0.18s ease",
        color: theme.color.ink,
      }}
    >
      <div style={{
        flex: 1, minWidth: 0,
        display: "flex", flexDirection: "column", gap: 6,
      }}>
        <div style={{
          alignSelf: "flex-start",
          fontFamily: FONT.forma,
          fontSize: 9,
          fontWeight: 600,
          color: accent,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          background: accentBg,
          border: `1px solid ${accentBorder}`,
          padding: "3px 8px",
          borderRadius: 4,
          lineHeight: 1.2,
        }}>
          {kicker}
        </div>
        <div style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: 18,
          lineHeight: 1.2,
          color: theme.color.ink,
        }}>
          {title}
        </div>
        {blurb && (
          <div style={{
            fontFamily: FONT.forma,
            fontSize: 12,
            color: theme.color.inkMuted || COLOR.muted,
            lineHeight: 1.5,
          }}>
            {blurb}
          </div>
        )}
      </div>
      {rightSlot != null
        ? rightSlot
        : (
          <span aria-hidden style={{
            alignSelf: "center",
            color: accent,
            fontFamily: FONT.forma,
            fontSize: 18,
            fontWeight: 600,
            flexShrink: 0,
          }}>→</span>
        )}
    </button>
  );
}
